import { useState } from 'react';
import { AlertCircle, HelpCircle, Loader2, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { value: 'interview_access',  label: 'Cannot access interview' },
  { value: 'technical_issue',   label: 'Technical / audio issue' },
  { value: 'scheduling',        label: 'Scheduling / timing issue' },
  { value: 'account',           label: 'Account problem' },
  { value: 'general',           label: 'General question' },
];

const PRIORITIES = [
  { value: 'low',    label: 'Low',    color: 'text-slate-500' },
  { value: 'medium', label: 'Medium', color: 'text-amber-600' },
  { value: 'high',   label: 'High',   color: 'text-orange-600' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
];

export default function RaiseTicketModal({ open, onClose, sessions = [], onCreated }) {
  const [sessionId, setSessionId]     = useState('');
  const [category,  setCategory]      = useState('interview_access');
  const [priority,  setPriority]      = useState('medium');
  const [subject,   setSubject]       = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');

  const reset = () => {
    setSessionId(''); setCategory('interview_access'); setPriority('medium');
    setSubject(''); setDescription(''); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!sessionId)     return setError('Please select an interview.');
    if (!subject.trim()) return setError('Please enter a subject.');
    if (description.trim().length < 20) return setError('Description must be at least 20 characters.');

    setSubmitting(true);
    try {
      await apiClient.createSupportTicket({
        type: 'candidate_to_company',
        sessionId,
        category,
        priority,
        subject:     subject.trim(),
        description: description.trim(),
      });
      toast.success('Support ticket raised! The company will respond shortly.');
      onCreated?.();
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to raise ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <HelpCircle className="h-4 w-4 text-primary" />
            </div>
            Raise a Support Ticket
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Describe your issue and the company support team will respond.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          {/* Interview selector */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              Interview <span className="text-destructive">*</span>
            </label>
            <select
              value={sessionId}
              onChange={e => setSessionId(e.target.value)}
              className={cn(
                'w-full rounded-md border border-border bg-background px-3 py-2 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary',
                'text-foreground'
              )}
            >
              <option value="">— Select an interview —</option>
              {sessions.map(s => (
                <option key={s._id} value={s._id}>
                  {s.template?.title || 'Interview'} ·{' '}
                  {s.scheduledAt ? new Date(s.scheduledAt).toLocaleDateString() : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Category + Priority row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              Subject <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Brief description of your issue"
              maxLength={200}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              Description <span className="text-destructive">*</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Explain your issue in detail — what happened, what you expected, any error messages..."
              rows={4}
              maxLength={4000}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
            />
            <p className="text-right text-[10px] text-muted-foreground">{description.length}/4000</p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting} className="gap-1.5">
              {submitting
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Submitting…</>
                : <><Send className="h-3.5 w-3.5" /> Submit Ticket</>
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
